import React, { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import useMedia from "react-use/lib/useMedia";

import { Button } from "@/components/Button";
import { FiltersActiveFilters, FiltersCol, FiltersCollapsible } from "@/components/Filters";
import { SubSection } from "@/components/Section";
import { Text } from "@/components/Text";
import { UserContext } from "@/components/User";
import { MEDIA_SIZE_SM } from "@/core/media";
import { ITasksURLFilter } from "@/data/tasks";
import { Flex, Row } from "@/eworkitComponents";
import {
  AssigneeFilter,
  CustomerFiler,
  TaskTypeFilter,
  TaskUrgencyFilter,
  UnassignedTasksFilter,
  UserTasksFilter,
} from "@/views/components/Filters";

import { useTasksActiveFilters } from "./useTasksActiveFilters";

import "./_filters.scss";

interface IFiltersProps {
  filtersValue: ITasksURLFilter;
  onFiltersUpdate: (value: any, cb?: () => void) => void;
  onFilterClear: () => void;
  isFilterOpen?: boolean;
  resultsCount?: number;
  hideClearFilters?: boolean;
}

export const FiltersComponent: React.FC<IFiltersProps> = ({
  filtersValue,
  onFiltersUpdate,
  onFilterClear,
  isFilterOpen,
  resultsCount,
}) => {
  const isSmallScreen = useMedia(MEDIA_SIZE_SM);
  const { t } = useTranslation(["session", "common"]);
  const User = useContext(UserContext);
  const companySearchParams = useMemo(() => ({ companyId: User.contextCompany?.id || 0 }), [User.contextCompany?.id]);

  const { activeBadges, filtersCount } = useTasksActiveFilters(filtersValue, onFiltersUpdate, User.sessionUser);

  return (
    <Flex direction="column" className="tasks-filters">
      <FiltersCollapsible collapsed={!isFilterOpen} appearance={isSmallScreen ? "mobile" : "desktop"}>
        <div className="tasks-filters__main">
          <SubSection
            background="background-card-bottom"
            role="top"
            padding={["md"]}
            border={[false, false, true, false]}>
            <Flex direction="row" justify="space-between" align="center">
              <Flex padding={["xs"]} innerMargin="lg" justify="center">
                <Text size="lg" color="black" weight="bold">
                  {t("TASKS.FILTERS.ACTIVE_TASKS_LABEL", { count: resultsCount || 0 })}
                </Text>
                {Boolean(filtersCount) && !isSmallScreen && (
                  <Button size="md" variant="link" onClick={onFilterClear}>
                    {t("common:COMPONENTS.FILTERS_CONTAINER.BUTTON_CLEAR_FILTERS")}
                  </Button>
                )}
              </Flex>
              <Flex direction="row">{/* render save filter select and button */}</Flex>
            </Flex>
          </SubSection>
          <Flex padding={["md"]} direction="column">
            <Row grow={1}>
              <FiltersCol width={{ xxs: 12, sm: 4 }} header={t("TASKS.FILTERS.TASK_TYPE_LABEL")}>
                <TaskTypeFilter name="types" value={filtersValue.types} onChange={onFiltersUpdate} />
              </FiltersCol>
              <FiltersCol width={{ xxs: 12, sm: 4 }} header={t("TASKS.FILTERS.TASK_URGENCY_LABEL")}>
                <TaskUrgencyFilter name="priorities" value={filtersValue.priorities} onChange={onFiltersUpdate} />
              </FiltersCol>
              <FiltersCol width={{ xxs: 12, sm: 4 }} header={t("TASKS.FILTERS.ASSIGNEE_LABEL")}>
                {Boolean(companySearchParams.companyId) && (
                  <AssigneeFilter
                    name="assignees"
                    value={filtersValue.assignees}
                    onChange={onFiltersUpdate}
                    searchParams={companySearchParams}
                  />
                )}
                <Flex direction={isSmallScreen ? "column" : "row"}>
                  <UserTasksFilter
                    name="withAssignee"
                    value={filtersValue.withAssignee}
                    currentAssignees={filtersValue.assignees}
                    currentUser={User.sessionUser}
                    onChange={onFiltersUpdate}
                  />
                  <UnassignedTasksFilter
                    name="withAssignee"
                    value={filtersValue.withAssignee === false}
                    onChange={onFiltersUpdate}
                  />
                </Flex>
              </FiltersCol>
            </Row>
            <Row>
              <FiltersCol width={{ xxs: 12, sm: 4 }} header={t("TASKS.FILTERS.CUSTOMER_LABEL")}>
                <CustomerFiler
                  name="originServices"
                  value={filtersValue.originServices}
                  onChange={onFiltersUpdate}
                  searchParams={companySearchParams}
                />
              </FiltersCol>
            </Row>
          </Flex>
        </div>
      </FiltersCollapsible>

      {!isFilterOpen && Boolean(activeBadges.length) && (
        <Flex margin={["md", false, false, false]} padding={isSmallScreen ? [false, "sm"] : undefined}>
          <FiltersActiveFilters activeFilters={activeBadges} onClearFilters={onFilterClear} />
        </Flex>
      )}
    </Flex>
  );
};

export const Filters = React.memo(FiltersComponent);
